/**
 * socket.ts — Browser-side Socket.io client (task G04).
 *
 * Socket.io is a notification channel, NOT the source of truth (TODO.md §7):
 *  - the client never sends identity information — the server resolves the
 *    user from the HttpOnly session cookie in the handshake (task G01);
 *  - every `game:changed` notification is followed by an HTTP refetch of
 *    the authoritative state (task G04);
 *  - room membership (`game:<id>`) is granted by the server only after a
 *    database membership check (task G02).
 *
 * `RealtimeSocket` is a minimal structural view of the socket.io-client
 * `Socket`: production code receives the real client, tests inject fakes
 * (no DOM/network needed for the React wiring tests).
 */
import { io as connectSocket } from 'socket.io-client';

export interface RealtimeSocket {
  readonly connected: boolean;
  on(event: string, listener: (...args: unknown[]) => void): unknown;
  off(event: string, listener?: (...args: unknown[]) => void): unknown;
  once(event: string, listener: (...args: unknown[]) => void): unknown;
  /** The last argument may be a server acknowledgement callback. */
  emit(event: string, ...args: unknown[]): unknown;
  connect(): unknown;
  disconnect(): unknown;
}

/** Socket factory. Injectable for tests; production default below. */
export type SocketFactory = () => RealtimeSocket;

/**
 * Production factory: same-origin Socket.io connection. The session cookie
 * (HttpOnly, set by the server) rides along automatically — no client-side
 * identity, tokens, or permissions are ever transmitted.
 */
export const createSocket: SocketFactory = () =>
  connectSocket({
    withCredentials: true,
    transports: ['websocket', 'polling'],
  });
